import workers from '@/workers'
import { log, debug } from '@/services/log'
import { useAuthStore } from '@/stores/auth'
import { watch } from 'vue'
import { generateStoreName } from '@/services/store-utils'

const apiUrl = import.meta.env.VUE_APP_API_URL
const rabbitDebug = import.meta.env.VUE_APP_RABBIT_DEBUG
const { stompWorker, syncWorker } = workers
const idbName = generateStoreName()
const handlers = {}

const config = {
  syncMilliSeconds: 300000,
  token: null,
  type: 'start',
  apiUrl,
  idbName,
}

const startSyncWorker = (value) => {
  if (value) {
    config.token = useAuthStore().token
    log('Worker started with config ', config)
    syncWorker.postMessage(config)
  } else {
    syncWorker.postMessage({ type: 'stop' })
    log('Sync Worker has been stopped due to non availability of an authenticated user.')
  }
}
const startStompWorker = (value, creds) => {
  if (value && creds) {
    const message = {
      broker: creds.host,
      vhost: creds.vhost,
      username: creds.username,
      password: creds.password,
      debug: rabbitDebug,
      type: 'connect',
    }
    log('Stomp Worker starting with config ', message)
    stompWorker.postMessage(message)
  } else {
    stompWorker.postMessage({ type: 'disconnect' })
    log('Stomp Worker has been stopped due to non availability of an authenticated user.')
  }
}

const provideStompClient = (app) => {
  app.provide('stompClient', {
    subscribe: (uri, handler) => {
      if (handlers[uri] == null) handlers[uri] = []
      handlers[uri].push(handler)
      stompWorker.postMessage({ type: 'subscribe', uri })
    },
    unsubscribe: (uri) => {
      for (const prop in handlers) delete handlers[prop]
      stompWorker.postMessage({ type: 'unsubscribe', uri })
    },
    send: (uri, payload) => {
      stompWorker.postMessage({ type: 'send', uri, payload })
    },
  })
}
const setupAuthStatusWatch = (authStore) => {
  watch(
    () => authStore.authStatus === 'SUCCESS',
    (value) => {
      startStompWorker(value, authStore.rabbitCreds)
      startSyncWorker(value)
    },
    { immediate: true }
  )
}
const createSyncWorkerHandler =
  (authStore, appStore, allIdbEntities) =>
  ({ data: { type, message, storeName, count } }) => {
    debug('Inside Handle syncWorker Message, Type', type, 'Message', message)
    if (type === 'started') {
      const syncableUris = allIdbEntities
        .filter((d) => d.uri)
        .map((d) => ({
          uri: d.uri,
          type: 'sync',
          objectStoreName: d.objectStoreName,
        }))
      syncableUris.forEach((d) => syncWorker.postMessage(d))
    } else if (type === 'tokenExpired') {
      authStore.logout()
    } else if (type === 'synced') {
      appStore?.updateIDBStoreRecordCount({
        storeName,
        count,
      })
    }
  }
const createStompWorkerHandler =
  (stompWorkerBinding) =>
  ({ data: { userUuids, issuer, type } }) => {
    log(
      'Stomp Manager message handler. type',
      type,
      'stompWorkerBinding',
      stompWorkerBinding,
      'handler',
      stompWorkerBinding[type]
    )
    stompWorkerBinding[type]({ userUuids, issuer })
  }
const createStompWorkerBinding = (appStore, authStore) => ({
  connectedOk: (msg) => {
    appStore?.stompConnected()
    log(msg)
  },
  connectionFailed: (msg) => {
    appStore?.stompDisconnected()
    log(msg)
  },
  subscriptionData: (message) => {
    const funcs = handlers[message.uri]
    funcs.forEach((fn) => fn(message))
  },
  forceLogout: ({ userUuids, issuer }) => {
    log(`Force logout for users: ${userUuids}, issued by: ${issuer}`, authStore)
    if (userUuids.includes(authStore.user.externalId)) {
      stompWorker.postMessage({
        type: 'disconnect',
        message: 'Closing the stomp connection as force logout is requested',
      })
      authStore.logout()
    }
  },
  rebuildCache: ({ userUuids, issuer }) => {
    log(`Rebuilding cache for users: ${userUuids}, issued by: ${issuer}`)
    if (userUuids.includes(authStore.user.externalId)) {
      syncWorker.postMessage({
        type: 'reset',
      })
    }
  },
  success: (msg) => log(msg),
  failure: (msg) => {
    log(msg)
  },
})
export function createWorkerSupervisor(app, appStoreFn, allIdbEntities) {
  const appStore = appStoreFn()
  const authStore = useAuthStore()

  syncWorker.onmessage = createSyncWorkerHandler(authStore, appStore, allIdbEntities)
  stompWorker.onmessage = createStompWorkerHandler(createStompWorkerBinding(appStore, authStore))
  setupAuthStatusWatch(authStore)
  provideStompClient(app)
}
